// packages/web/src/components/teach/KnowledgePointsModal.tsx
import React from 'react';
import { useTeacherLearningStore } from '../../stores/teacherLearningStore';
import { MathRenderer } from '../common/MathRenderer.v3';

export const KnowledgePointsModal: React.FC = () => {
  const {
    selectedExerciseForDetail,
    setShowKnowledgePointsModal,
    setSelectedExerciseForDetail,
  } = useTeacherLearningStore();

  if (!selectedExerciseForDetail) {
    return null;
  }

  const handleClose = () => {
    setShowKnowledgePointsModal(false);
    setSelectedExerciseForDetail(null);
  };

  const getDifficultyLabel = (difficulty: number) => {
    const labels: Record<number, string> = {
      1: '基础',
      2: '中等',
      3: '较高',
    };
    return labels[difficulty] || '未知';
  };

  const getDifficultyColor = (difficulty: number) => {
    const colors: Record<number, string> = {
      1: 'bg-green-100 text-green-800',
      2: 'bg-yellow-100 text-yellow-800',
      3: 'bg-red-100 text-red-800',
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="knowledge-points-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="modal-content bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* 模态窗口头部 */}
        <div className="modal-header flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {selectedExerciseForDetail.title}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* 模态窗口主体 */}
        <div className="modal-body flex-1 overflow-y-auto p-6 space-y-6">
          {/* 题目内容 */}
          <div className="section">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">题目内容</h3>
            <div className="content p-4 bg-gray-50 rounded-lg border border-gray-200">
              <MathRenderer
                content={selectedExerciseForDetail.initial_question}
                className="text-sm text-gray-800"
              />
            </div>
          </div>

          {/* 学科分类 */}
          <div className="section">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">学科分类</h3>
            <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
              {selectedExerciseForDetail.category}
            </span>
          </div>

          {/* 教学要求 */}
          {selectedExerciseForDetail.teacher_requirement && (
            <div className="section">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">教学要求</h3>
              <div className="content p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-800">
                  {selectedExerciseForDetail.teacher_requirement}
                </p>
              </div>
            </div>
          )}

          {/* 知识点列表 */}
          {selectedExerciseForDetail.knowledge_points && selectedExerciseForDetail.knowledge_points.length > 0 && (
            <div className="section">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                知识点（{selectedExerciseForDetail.knowledge_points.length}）
              </h3>
              <div className="knowledge-points-list space-y-2">
                {selectedExerciseForDetail.knowledge_points.map((kp, index) => (
                  <div
                    key={index}
                    className="knowledge-point flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <span className="text-sm text-gray-800">{kp.name}</span>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(
                        kp.difficulty
                      )}`}
                    >
                      {getDifficultyLabel(kp.difficulty)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 如果没有知识点 */}
          {(!selectedExerciseForDetail.knowledge_points || selectedExerciseForDetail.knowledge_points.length === 0) && (
            <div className="section">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">知识点</h3>
              <div className="content p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                <p className="text-sm text-gray-500">暂无知识点信息</p>
              </div>
            </div>
          )}
        </div>

        {/* 模态窗口底部 */}
        <div className="modal-footer flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};
